import type Flickity from "flickity";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { ComparisonCarouselBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { useFlickity } from "../../hooks/useFlickity";
import { getSpacingClasses } from "../_spacing/spacing";

type IProps = Immutable<{
    value: Value;
}>;

type ComparisonProduct = NonNullable<Value["comparison_products"]>[number];
type ProductImage = NonNullable<ComparisonProduct["image_a"]>;

const renderProductImage = (
    image: Immutable<ProductImage> | null | undefined,
): React.JSX.Element | null => {
    if (!image) {
        return null;
    }
    const r = image.renditions;
    // Use each rendition's *actual* pixel width for the `w` descriptor. Wagtail
    // never upscales, so e.g. a `width-1000` rendition of a 500px-wide source is
    // really 500px; labeling it `1000w` overstates the file size and makes the
    // browser compute a halved intrinsic width. This mirrors the original
    // `wagtail_image` tag, which built its srcset from `r.width`.
    const srcSet = [250, 500, 1000]
        .map((w) => {
            const rendition = r[`width-${w}`];
            return rendition?.url ? `${rendition.url} ${rendition.width}w` : "";
        })
        .filter(Boolean)
        .join(",");
    // Pin the intrinsic `width` to the `src` rendition's real pixel width.
    // Without it, a native `srcSet` of `w` descriptors with no `sizes`
    // (defaults to `100vw`) and no CSS width makes the browser compute a
    // density-corrected intrinsic width of 100vw -- the image balloons to the
    // full viewport instead of its natural size. The legacy `{% wagtail_image %}`
    // markup emitted no native srcset (lazysizes used `data-srcset`/`data-sizes`),
    // so its images fell back to their natural width. A `width` attribute is a
    // presentational hint that the author rule `.comparison-carousel-block__product
    // img { width: 100% }` still overrides on sites that load that SCSS
    // We set both `width` and `height` to reserve the image's aspect-ratio
    // before the lazy image loads. Flickity uses the image height to size the
    // carousel, so without an intrinsic height it may initialize with a
    // too-short viewport. `height: auto` preserves the aspect ratio after load.
    const largest = r["width-1000"];
    return (
        <img
            src={largest?.url ?? image.url}
            srcSet={srcSet || undefined}
            loading="lazy"
            decoding="async"
            width={largest?.width}
            height={largest?.height}
            alt={image.title}
            aria-hidden="true"
        />
    );
};

export const ComparisonCarouselBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const products = v.comparison_products ?? [];
    const totalSlides = products.length;
    const slidesRef = React.useRef<HTMLDivElement | null>(null);
    const dotsRef = React.useRef<HTMLDivElement | null>(null);

    const syncDots = React.useCallback((selectedIndex: number): void => {
        const dots = dotsRef.current?.children;
        if (!dots) {
            return;
        }

        Array.from(dots).forEach((dot, index) => {
            const isSelected = index === selectedIndex;
            dot.classList.toggle("is-selected", isSelected);
            dot.setAttribute("aria-current", isSelected ? "true" : "false");
        });
    }, []);

    const handleFlickityInit = React.useCallback(
        (flickity: Flickity): (() => void) => {
            const handleSelect = (index: number): void => {
                syncDots(index);
            };

            syncDots(flickity.selectedIndex);
            flickity.on("select", handleSelect);

            return () => {
                flickity.off("select", handleSelect);
            };
        },
        [syncDots],
    );

    const { flickityRef } = useFlickity(slidesRef, {
        options: {
            watchCSS: true,
            autoPlay: false,
            imagesLoaded: true,
            pageDots: false,
            prevNextButtons: false,
        },
        enabled: products.length > 0,
        deps: [products.length],
        onInit: handleFlickityInit,
    });

    return (
        <div
            role="region"
            aria-label={t`carousel`}
            className={concatClassNames([
                "comparison-carousel-block",
                v.spacing ? getSpacingClasses(v.spacing) : "",
            ])}
        >
            <header>
                {v.header_content && <RichTextBlock value={v.header_content} />}
            </header>
            <div className="comparison-carousel-block__wrapper">
                <button
                    className="comparison-carousel-block__button comparison-carousel-block__button--prev"
                    type="button"
                    aria-label={t`Previous`}
                    onClick={() => flickityRef.current?.previous()}
                >
                    <svg
                        className="comparison-carousel-block__button-icon"
                        viewBox="0 0 100 100"
                    >
                        <path
                            d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z"
                            className="arrow"
                        ></path>
                    </svg>
                </button>
                <div
                    className="comparison-carousel-block__slides"
                    ref={slidesRef}
                >
                    {/* Index key: ListBlock children don't surface a stable
                        id, so CMS reorders can reuse the wrong slide DOM /
                        Flickity state. Proper fix requires exposing an id
                        from the Python block (via get_api_representation). */}
                    {products.map((product, index) => (
                        <div
                            key={index}
                            className="comparison-carousel-block__slide"
                        >
                            <div className="comparison-carousel-block__product">
                                {renderProductImage(product.image_a)}
                                <p>{product.desc_a}</p>
                            </div>
                            <div className="comparison-carousel-block__product">
                                {renderProductImage(product.image_b)}
                                <p>{product.desc_b}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <button
                    className="comparison-carousel-block__button comparison-carousel-block__button--next"
                    type="button"
                    aria-label={t`Next`}
                    onClick={() => flickityRef.current?.next()}
                >
                    <svg
                        className="comparison-carousel-block__button-icon"
                        viewBox="0 0 100 100"
                    >
                        <path
                            d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z"
                            className="arrow"
                            transform="translate(100, 100) rotate(180)"
                        ></path>
                    </svg>
                </button>
                <div className="comparison-carousel-block__dots" ref={dotsRef}>
                    {products.map((_, index) => {
                        const slideNumber = index + 1;
                        return (
                            <button
                                key={index}
                                type="button"
                                className={index === 0 ? "is-selected" : ""}
                                aria-current={index === 0 ? "true" : "false"}
                                aria-label={t`Slide ${slideNumber} of ${totalSlides}`}
                                onClick={() =>
                                    flickityRef.current?.select(index)
                                }
                            ></button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
